import React, { useEffect, useState } from 'react';
import MoonLoader from 'react-spinners/MoonLoader';
import useClinics from '../../store/clinics/clinicsContext';
import { useHistory } from 'react-router';
import { CLINIC_TYPES, COLORS } from '../../config';
import { COUNTRIES } from '../../helpers/selectOptions';
import FieldWithEdit from '../../components/FieldItems/FieldWithEdit';
import EditIcon from '../../components/Icons/outline/EditIcon';
import PrimaryButton from '../../components/Buttons/PrimaryButton';
import FieldWithSelectEdit from '../../components/FieldItems/FieldWithSelectEdit';
import RedButton from '../../components/Buttons/RedButton';
import DeleteClinicModal from './DeleteClinicModal';
import CollaboratorCard from '../../components/Cards/CollaboratorCard';
import AddClinicPartnerModal from './AddClinicPartnerModal';

function ViewClinicDetails({
  clinicId : id
}) {

  const history = useHistory();

  const {
    editClinic,
    deleteClinic,
    getClinicDetails,
    addClinicPartner,
    removeClinicPartner
  } = useClinics();

  const [isLoading, setIsLoading] = React.useState(true);

  const [clinic, setClinic] = React.useState({});
  const [partners, setPartners] = React.useState([]);
  const [edit, setEdit] = React.useState(false);
  const [showAddClinicPartnerModal, setShowAddClinicPartnerModal] = React.useState(false);

  useEffect(() => {
    (async function fetchDetails() {
      await getClinicDetails(id).then(res => {
        setClinic(res?.data.clinic);
        setPartners(res?.data.partnerInfo);
        setIsLoading(false);
      }).catch(() => {
        history.push('/manage-clinics')
      })
    })();
  
  }, [id])

  const [clinicEdits, _setClinicEdits] = useState();

  const setClinicEdits = (newVal) => _setClinicEdits({ ...clinicEdits, ...newVal });

  const [showDeleteClinicModal, setShowDeleteClinicModal] = React.useState(false);

  const handleUpdateClick = () => {
    const { type, name, location, phone_nb, email} = clinicEdits;
    const formattedBody = {
      type,
      name,
      location,
      phone_nb,
      email,
    };
    editClinic(clinic._id, formattedBody).then((res) => {
      setClinic(res.data);
    });
    setEdit(false);
  }

  const handleEditClick = (field) => {
    setClinicEdits(clinic); 
    if (edit === field) setEdit(false)
    if (edit !== field) setEdit(field)
  }
  
  const handleDelete = () => {
    deleteClinic(clinic._id).then(() => history.push('/manage-clinics'));
  }

  const countryOptions = [];
  if (!countryOptions.length) COUNTRIES.forEach(country => {
    countryOptions.push({
      value: country,
      label: country
    })
  });

  const clinicTypeOptions = [];
  if (!clinicTypeOptions.length) Object.entries(CLINIC_TYPES).map(([key, value]) => (
    clinicTypeOptions.push({
      label: value,
      value: value
    })
  ));

  const handleAddClinicPartner = (partnerId) => {
    addClinicPartner(id, partnerId).then(res => {
      const newPartner = res.data?.partner;
      setPartners([...partners, newPartner ]);
    })
    setShowAddClinicPartnerModal(false);
  }

  const handleRemoveClinicPartner = (partnerId) => {
    removeClinicPartner(id, partnerId).then(() => {
      const newArray = partners.filter((partner) => partner.user_id._id !== partnerId);
      setPartners(newArray);
    })
  }

  if (isLoading) return (
    <div className='flex justify-around items-center h-full'>
      <MoonLoader color={COLORS.colorPrimary}/>
    </div>
  )

  return (
    <div className="flex flex-col">
      <AddClinicPartnerModal 
        showAddClinicPartner={showAddClinicPartnerModal} 
        setShowAddClinicPartner={setShowAddClinicPartnerModal} 
        onAdd={handleAddClinicPartner}
        clinicId={id}
        clinicPartners={partners}
      />
      <DeleteClinicModal showDeleteClinicModal={showDeleteClinicModal} setShowDeleteClinicModal={setShowDeleteClinicModal} onDelete={handleDelete}/>
      <div className="bg-white rounded shadow py-12 px-10">
        <div className='flex justify-between items-center mb-10'>
          <h1 className=" text-2xl text-secondary">Clinic information</h1>
          <div className={`${edit === 'clinicInfo' ? 'rounded-full h-10 w-10 bg-primary flex justify-center items-center' : 'rounded-full h-10 w-10 bg-white flex justify-center items-center'}`}>
              <EditIcon stroke={edit === 'clinicInfo' ? 'white' : COLORS.colorSecondary} onClick={() => handleEditClick('clinicInfo')} className='cursor-pointer'/>
          </div>
        </div>
        <div className='grid grid-cols-1 gap-y-4'>  
          <FieldWithEdit label='Name' data={edit === 'clinicInfo' ? clinicEdits.name : clinic.name} onChange={(e) => setClinicEdits({name: e.target.value})} edit={edit === 'clinicInfo'}/>
          <FieldWithSelectEdit label='Type' options={clinicTypeOptions} data={edit === 'clinicInfo' ? clinicEdits.type : clinic.type} onChange={(e) => setClinicEdits({type:  e.value})} edit={edit === 'clinicInfo'}/> 
          <FieldWithEdit label='Email' data={edit === 'clinicInfo' ? clinicEdits.email : clinic.email} onChange={(e) => setClinicEdits({email: e.target.value})} edit={edit === 'clinicInfo'}/>
          
          {edit === 'clinicInfo' && 
          <div className='flex flex-row-reverse'>
            <div className='w-32'>
              <PrimaryButton className="mt-8" text='Save' onClick={handleUpdateClick} />
            </div>
          </div>}
        </div>
      </div>
      <div className="bg-white rounded shadow py-12 px-10 mt-6">
        <div className='flex justify-between items-center mb-10'>
          <h1 className=" text-2xl text-secondary">Contact information</h1>
          <div className={`${edit === 'contactInfo' ? 'rounded-full h-10 w-10 bg-primary flex justify-center items-center' : 'rounded-full h-10 w-10 bg-white flex justify-center items-center'}`}>
              <EditIcon stroke={edit === 'contactInfo' ? 'white' : COLORS.colorSecondary} onClick={() => handleEditClick('contactInfo')} className='cursor-pointer'/>
          </div>
        </div>
        <div className='grid grid-cols-1 gap-y-4'>  
          <FieldWithSelectEdit label='Country' options={countryOptions} data={edit === 'contactInfo' ? clinicEdits.location.country : clinic.location.country} onChange={(e) => setClinicEdits({location: {...clinicEdits.location, country: e.value}})} edit={edit === 'contactInfo'}/> 
          <FieldWithEdit label='City' data={edit === 'contactInfo' ? clinicEdits.location.city : clinic.location.city} onChange={(e) => setClinicEdits({location: {...clinicEdits.location, city: e.target.value}})} edit={edit === 'contactInfo'}/>
          <FieldWithEdit label='Address' data={edit === 'contactInfo' ? clinicEdits.location.address : clinic.location.address} onChange={(e) => setClinicEdits({location: {...clinicEdits.location, address: e.target.value}})} edit={edit === 'contactInfo'}/>
          <FieldWithEdit label='Postal code' data={edit === 'contactInfo' ? clinicEdits.location.postal_code : clinic.location.postal_code} onChange={(e) => setClinicEdits({location: {...clinicEdits.location, postal_code: e.target.value}})} edit={edit === 'contactInfo'}/>
          <FieldWithEdit label='Phone' data={edit === 'contactInfo' ? clinicEdits.phone_nb : clinic.phone_nb} onChange={(e) => setClinicEdits({phone_nb: e.target.value})} edit={edit === 'contactInfo'}/>
          {edit === 'contactInfo' && 
          <div className='flex flex-row-reverse'>
            <div className='w-32'>
              <PrimaryButton className="mt-8" text='Save' onClick={handleUpdateClick} />
            </div>
          </div>}
        </div>
      </div>
      <div className="bg-white rounded shadow py-12 px-10 mt-6">
        <div className='flex justify-between items-center mb-10'>
          <h1 className="text-2xl text-secondary">Clinic collaborators</h1>
          <div className={`${edit === 'clinicInfo' ? 'rounded-full h-10 w-10 bg-primary flex justify-center items-center' : 'rounded-full h-10 w-10 bg-white flex justify-center items-center'}`}>
              <EditIcon stroke={edit === 'clinicInfo' ? 'white' : COLORS.colorSecondary} onClick={() => handleEditClick('clinicInfo')} className='cursor-pointer'/>
          </div>
        </div>
        <div className='grid grid-cols-1 gap-y-4'>  
          {partners && 
          <div className='px-8 xl:px-12'>
            {partners.map((partner, key) => {
              return (
              <div className='mt-2'>
                <CollaboratorCard collaborator={partner} key={key} onDelete={(edit === 'clinicInfo') ? () => handleRemoveClinicPartner(partner.user_id._id) : false}/>
              </div>
              )
            })}
          </div>
          }
          {!partners.length && <span>No partners currently registered on this clinic.</span>}
          {edit === 'clinicInfo' && 
          <div className='flex flex-row-reverse'>
            <div className='w-32'>
              <PrimaryButton className="mt-8" text='Add' onClick={() => setShowAddClinicPartnerModal(true)} />
            </div>
          </div>}
        </div>
      </div>

      <div className='w-full flex flex-row-reverse pb-4'>
        <PrimaryButton className="w-32 mt-8" text='Back' onClick={() => history.push('/manage-clinics')} />
        <RedButton className="w-32 mr-4 mt-8" text={'Delete clinic'} onClick={() => setShowDeleteClinicModal(true)} />
      </div>
    </div>
  )
}

export default ViewClinicDetails
